import { useEffect, useState } from 'react';

import { adminApi, type AdminSearchResults } from '@/services/adminApi';

const EMPTY: AdminSearchResults = { customers: [], users: [], count: 0 };
const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

/**
 * useAdminSearch — debounced search against `/admin/search?type=customer`.
 *
 * Used by the AdminLayout header search bar (D1.3 = exact-match
 * in-memory). Requires at least 2 characters before firing the
 * request — keeps the BE quiet for "noise" keystrokes.
 *
 * Debounce + AbortController follow the same pattern as
 * `useAddressAutocomplete` (Plan B) so racing typists don't cause
 * order-of-arrival flicker.
 */
export function useAdminSearch(query: string): {
  results: AdminSearchResults;
  isLoading: boolean;
} {
  const [results, setResults] = useState<AdminSearchResults>(EMPTY);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults(EMPTY);
      setIsLoading(false);
      return;
    }
    const ctrl = new AbortController();
    setIsLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await adminApi.search(trimmed, 'customer');
        if (!ctrl.signal.aborted) setResults(data);
      } catch {
        if (!ctrl.signal.aborted) setResults(EMPTY);
      } finally {
        if (!ctrl.signal.aborted) setIsLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
      setIsLoading(false);
    };
  }, [query]);

  return { results, isLoading };
}
